/* eslint-disable vue/html-self-closing */
<template>
  <v-layout>
    <div>
      <!-- Hero band: lighter navy than the nav, geometric logo watermark -->
      <header
        class="public-hero"
        :class="{ 'is-agent-hero': heroAgent }"
      >
        <div class="container public-hero-flex">
          <!-- Single-agent page: the agent IS the hero -->
          <template v-if="heroAgent">
            <div class="hero-agent-identity">
              <img
                class="hero-agent-photo"
                :src="heroAgent.avatar_url"
                :alt="heroAgent.name"
              >
              <div>
                <p class="public-hero-kicker hero-agent-kicker">
                  {{ $t("rent_agent_title") }}
                </p>
                <h1 class="public-hero-title">
                  {{ heroAgent.name }}
                </h1>
              </div>
            </div>
            <button
              class="hero-all-listings"
              type="button"
              @click="clearAgent"
            >
              <b-icon
                icon="arrow-left"
                size="is-small"
              />
              {{ $t("see_all_listings") }}
            </button>
          </template>
          <div v-else>
            <h1 class="public-hero-title">
              {{ heroTitle }}
            </h1>
            <p class="public-hero-kicker">
              {{ heroKicker }}
            </p>
          </div>
        </div>
      </header>

      <div class="section">
        <div class="container">
          <!-- Top bar: type + address search -->
          <div class="columns mb-5 search-topbar">
            <div class="column filter-width-column is-hidden-mobile">
              <div class="field">
                <label class="label filter-label">
                  {{ $t("type").capitalize() }}
                  <span class="multi-select-hint">({{ $t("selection_multiple") }})</span>
                </label>
                <b-dropdown
                  v-model="parameters.filter.type"
                  class="multi-select-dropdown"
                  multiple
                  expanded
                  scrollable
                  max-height="300"
                  aria-role="list"
                >
                  <template #trigger>
                    <button
                      class="button is-fullwidth is-justify-content-space-between multi-select-trigger"
                      type="button"
                    >
                      <span class="multi-select-label">{{ typeLabel }}</span>
                      <b-icon icon="menu-down" />
                    </button>
                  </template>
                  <b-dropdown-item
                    v-for="(type, key) in publicPropertyTypes"
                    :key="key"
                    :value="type"
                    aria-role="listitem"
                  >
                    <span class="multi-select-option">
                      <span>{{ $t(type) }}</span>
                      <b-icon
                        :icon="parameters.filter.type.includes(type) ? 'checkbox-marked' : 'checkbox-blank-outline'"
                        size="is-small"
                        class="multi-select-check"
                      />
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
            <div class="column">
              <div
                ref="searchSuggestField"
                class="field search-suggest"
              >
                <label class="label filter-label" for="search">
                  {{ (isMobileView ? $t("tab_address") : $t("search_address_region")).capitalize() }}
                </label>
                <!-- Mobile: a plain address input (quartiers and villes are
                     multi-selects in the Filtres panel); desktop keeps the
                     Tous / Quartier / Ville / Adresse suggestions -->
                <b-input
                  v-model="searchText"
                  type="search"
                  icon="magnify"
                  :placeholder="isMobileView ? $t('address_search_hint') : $t('search_address_region')"
                  @focus="onSearchFocus"
                  @blur="onSearchBlur"
                  @keyup.enter.native="applySearch"
                />

                <!-- Suggestions: Tous / Quartier / Ville / Adresse tabs.
                     On mobile the panel outlives the input focus: touching
                     the list dismisses the keyboard (panel grows), and only
                     tapping outside closes it -->
                <div
                  v-if="suggestionsOpen"
                  class="search-suggest-panel"
                  :style="suggestPanelStyle"
                  @touchstart="onPanelTouch"
                >
                  <div
                    v-if="!isMobileView"
                    class="search-tabs"
                  >
                    <button
                      v-for="t in searchTabs"
                      :key="t.key"
                      type="button"
                      class="search-tab"
                      :class="{ 'is-active': searchTab === t.key }"
                      @mousedown.prevent="selectSearchTab(t.key)"
                    >
                      {{ t.label }}
                    </button>
                  </div>

                  <!-- Tous : quartiers + villes + adresses ensemble -->
                  <template v-if="searchTab === 'all'">
                    <template v-if="allTabRegionSuggestions.length">
                      <p class="search-suggest-group">{{ $t("group_regions") }}</p>
                      <div
                        v-for="r in allTabRegionSuggestions"
                        :key="'ar' + r.id"
                        class="search-suggest-item"
                        @mousedown.prevent="toggleRegion(r.id)"
                      >
                        <span class="search-suggest-name">
                          <b-icon
                            :icon="parameters.filter.region_id.includes(r.id) ? 'checkbox-marked' : 'checkbox-blank-outline'"
                            size="is-small"
                            class="mr-1"
                          />
                          {{ r.name.capitalize() }}
                        </span>
                        <span class="search-suggest-count">
                          {{ r.count }} {{ $t(r.count > 1 ? "dwellings_plural" : "dwelling_singular") }}
                        </span>
                      </div>
                    </template>
                    <template v-if="allTabCitySuggestions.length">
                      <p class="search-suggest-group">{{ $t("group_cities") }}</p>
                      <div
                        v-for="c in allTabCitySuggestions"
                        :key="'ac' + c.name"
                        class="search-suggest-item"
                        @mousedown.prevent="toggleCity(c.name)"
                      >
                        <span class="search-suggest-name">
                          <b-icon
                            :icon="parameters.filter.city.includes(c.name) ? 'checkbox-marked' : 'checkbox-blank-outline'"
                            size="is-small"
                            class="mr-1"
                          />
                          {{ c.name }}
                        </span>
                        <span class="search-suggest-count">
                          {{ c.count }} {{ $t(c.count > 1 ? "dwellings_plural" : "dwelling_singular") }}
                        </span>
                      </div>
                    </template>
                    <template v-if="allTabAddressSuggestions.length">
                      <p class="search-suggest-group">{{ $t("group_addresses") }}</p>
                      <div
                        v-for="a in allTabAddressSuggestions"
                        :key="'aa' + a.slug"
                        class="search-suggest-item"
                        @mousedown.prevent="$router.push('/' + a.slug)"
                      >
                        <span class="search-suggest-name">
                          <b-icon
                            icon="map-marker"
                            size="is-small"
                            class="mr-1"
                          />
                          {{ a.label }}
                        </span>
                      </div>
                    </template>
                    <div
                      v-if="!allTabRegionSuggestions.length && !allTabCitySuggestions.length && !allTabAddressSuggestions.length"
                      class="search-suggest-empty"
                    >
                      {{ $t("no_result_suggest") }}
                    </div>
                  </template>

                  <!-- Quartier -->
                  <template v-else-if="searchTab === 'region'">
                    <div
                      v-for="r in regionSuggestions"
                      :key="'r' + r.id"
                      class="search-suggest-item"
                      @mousedown.prevent="toggleRegion(r.id)"
                    >
                      <span class="search-suggest-name">
                        <b-icon
                          :icon="parameters.filter.region_id.includes(r.id) ? 'checkbox-marked' : 'checkbox-blank-outline'"
                          size="is-small"
                          class="mr-1"
                        />
                        {{ r.name.capitalize() }}
                      </span>
                      <span class="search-suggest-count">
                        {{ r.count }} {{ $t(r.count > 1 ? "dwellings_plural" : "dwelling_singular") }}
                      </span>
                    </div>
                    <div
                      v-if="!regionSuggestions.length"
                      class="search-suggest-empty"
                    >
                      {{ $t("no_result_suggest") }}
                    </div>
                  </template>

                  <!-- Ville -->
                  <template v-else-if="searchTab === 'city'">
                    <div
                      v-for="c in citySuggestions"
                      :key="'c' + c.name"
                      class="search-suggest-item"
                      @mousedown.prevent="toggleCity(c.name)"
                    >
                      <span class="search-suggest-name">
                        <b-icon
                          :icon="parameters.filter.city.includes(c.name) ? 'checkbox-marked' : 'checkbox-blank-outline'"
                          size="is-small"
                          class="mr-1"
                        />
                        {{ c.name }}
                      </span>
                      <span class="search-suggest-count">
                        {{ c.count }} {{ $t(c.count > 1 ? "dwellings_plural" : "dwelling_singular") }}
                      </span>
                    </div>
                    <div
                      v-if="!citySuggestions.length"
                      class="search-suggest-empty"
                    >
                      {{ $t("no_result_suggest") }}
                    </div>
                  </template>

                  <!-- Adresse -->
                  <template v-else>
                    <div
                      v-if="(searchText || '').length < 2"
                      class="search-suggest-empty"
                    >
                      {{ $t("address_search_hint") }}
                    </div>
                    <div
                      v-for="a in addressSuggestions"
                      :key="a.slug"
                      class="search-suggest-item"
                      @mousedown.prevent="$router.push('/' + a.slug)"
                    >
                      <span class="search-suggest-name">
                        <b-icon
                          icon="map-marker"
                          size="is-small"
                          class="mr-1"
                        />
                        {{ a.label }}
                      </span>
                    </div>
                    <div
                      v-if="(searchText || '').length >= 2 && !addressSuggestions.length"
                      class="search-suggest-empty"
                    >
                      {{ $t("no_result_suggest") }}
                    </div>
                  </template>

                  <!-- Mobile: sticky footer to apply and close the panel -->
                  <button
                    type="button"
                    class="search-apply-btn is-hidden-tablet"
                    @mousedown.prevent="applySearch"
                  >
                    {{ $t("search") }}
                  </button>
                </div>

                <!-- Selected regions / active agent as removable chips -->
                <div
                  v-if="selectedRegions.length || parameters.filter.city.length"
                  class="region-chips"
                >
                  <span
                    v-for="c in parameters.filter.city"
                    :key="'city' + c"
                    class="region-chip"
                  >
                    {{ c }}
                    <button
                      class="delete is-small ml-1"
                      type="button"
                      @click="toggleCity(c)"
                    />
                  </span>
                  <span
                    v-for="r in selectedRegions"
                    :key="r.id"
                    class="region-chip"
                  >
                    {{ r.name.capitalize() }}
                    <button
                      class="delete is-small ml-1"
                      type="button"
                      @click="toggleRegion(r.id)"
                    />
                  </span>
                </div>
              </div>
            </div>
            <div class="column is-narrow sort-column">
              <div class="field">
                <label class="label filter-label sort-spacer-label">&nbsp;</label>
                <b-dropdown
                  v-model="sortMode"
                  class="multi-select-dropdown"
                  position="is-bottom-left"
                  aria-role="list"
                >
                  <template #trigger>
                    <button
                      class="button is-fullwidth is-justify-content-space-between multi-select-trigger sort-trigger"
                      type="button"
                    >
                      <b-icon
                        icon="sort"
                        size="is-small"
                      />
                      <span class="multi-select-label mx-2">{{ sortLabel }}</span>
                      <b-icon icon="menu-down" />
                    </button>
                  </template>
                  <b-dropdown-item
                    v-for="opt in sortOptions"
                    :key="opt.value"
                    :value="opt.value"
                    aria-role="listitem"
                  >
                    <span class="multi-select-option">
                      <span>{{ opt.label }}</span>
                      <b-icon
                        v-if="sortMode === opt.value"
                        icon="check"
                        size="is-small"
                        class="multi-select-check"
                      />
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
            <!-- Mobile: Filtres toggle sits beside Trier (50/50) -->
            <div class="column is-narrow sort-column filters-column is-hidden-tablet">
              <div class="field">
                <b-button
                  class="mobile-filters-btn"
                  :icon-left="showMobileFilters ? 'chevron-up' : 'tune'"
                  expanded
                  @click="showMobileFilters = !showMobileFilters"
                >
                  {{ $t("filters").capitalize() }}
                </b-button>
              </div>
            </div>
            <div class="column is-narrow sort-column view-column is-hidden-mobile">
              <div class="field">
                <label class="label filter-label sort-spacer-label">&nbsp;</label>
                <div class="view-toggle">
                  <button
                    type="button"
                    class="view-toggle-btn"
                    :class="{ 'is-active': viewMode === 'list' }"
                    @click="viewMode = 'list'"
                  >
                    <b-icon
                      icon="view-list"
                      size="is-small"
                    />
                    <span>{{ $t("view_list").capitalize() }}</span>
                  </button>
                  <button
                    type="button"
                    class="view-toggle-btn"
                    :class="{ 'is-active': viewMode === 'map' }"
                    @click="viewMode = 'map'"
                  >
                    <b-icon
                      icon="map-outline"
                      size="is-small"
                    />
                    <span>{{ $t("view_map").capitalize() }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="columns">
            <!-- Left filter sidebar -->
            <aside
              class="column filter-width-column filter-sidebar"
              :class="{ 'is-collapsed-mobile': !showMobileFilters, 'is-open-mobile': showMobileFilters }"
            >
              <!-- Type (mobile: lives inside the Filtres panel; the top bar
                   column is hidden below 769px) -->
              <div class="field is-hidden-tablet">
                <label class="label filter-label">
                  {{ $t("type").capitalize() }}
                  <span class="multi-select-hint">({{ $t("selection_multiple") }})</span>
                </label>
                <b-dropdown
                  ref="mobileTypeDd"
                  @active-change="onMobileDropdownActive($event, 'mobileTypeDd')"
                  v-model="parameters.filter.type"
                  class="multi-select-dropdown"
                  multiple
                  expanded
                  scrollable
                  max-height="300"
                  aria-role="list"
                >
                  <template #trigger>
                    <button
                      class="button is-fullwidth is-justify-content-space-between multi-select-trigger"
                      type="button"
                    >
                      <span class="multi-select-label">{{ typeLabel }}</span>
                      <b-icon icon="menu-down" />
                    </button>
                  </template>
                  <!-- Own scrollbar (phones hide the native one until you scroll);
                       the native bar is hidden so there is only one -->
                  <b-dropdown-item
                    custom
                    paddingless
                    class="dd-scroll-holder"
                    aria-role="listitem"
                  >
                    <div
                      v-if="ddScroll.mobileTypeDd"
                      class="dd-scroll-track"
                      :style="{ height: ddScroll.mobileTypeDd.track + 'px' }"
                    >
                      <div
                        class="dd-scroll-thumb"
                        :style="{ height: ddScroll.mobileTypeDd.thumb + 'px', transform: 'translateY(' + ddScroll.mobileTypeDd.top + 'px)' }"
                      />
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-for="(type, key) in publicPropertyTypes"
                    :key="'mf' + key"
                    :value="type"
                    aria-role="listitem"
                  >
                    <span class="multi-select-option">
                      <span>{{ $t(type) }}</span>
                      <b-icon
                        :icon="parameters.filter.type.includes(type) ? 'checkbox-marked' : 'checkbox-blank-outline'"
                        size="is-small"
                        class="multi-select-check"
                      />
                    </span>
                  </b-dropdown-item>
                  <!-- Mobile: explicit close, like the Filtres panel's Terminé -->
                  <b-dropdown-item
                    custom
                    paddingless
                    class="multi-select-done"
                    aria-role="listitem"
                  >
                    <button
                      type="button"
                      class="multi-select-done-btn"
                      @click="$refs.mobileTypeDd.toggle()"
                    >
                      {{ $t("done") }}
                    </button>
                  </b-dropdown-item>
                </b-dropdown>
              </div>

              <!-- Mobile: Quartier and Ville are multi-selects here (desktop
                   picks them from the search suggestions) -->
              <div class="field is-hidden-tablet">
                <label class="label filter-label">
                  {{ $t("tab_region").capitalize() }}
                  <span class="multi-select-hint">({{ $t("selection_multiple") }})</span>
                </label>
                <b-dropdown
                  ref="mobileRegionDd"
                  @active-change="onMobileDropdownActive($event, 'mobileRegionDd')"
                  v-model="parameters.filter.region_id"
                  class="multi-select-dropdown"
                  multiple
                  expanded
                  scrollable
                  max-height="300"
                  aria-role="list"
                >
                  <template #trigger>
                    <button
                      class="button is-fullwidth is-justify-content-space-between multi-select-trigger"
                      type="button"
                    >
                      <span class="multi-select-label">{{ regionLabel }}</span>
                      <b-icon icon="menu-down" />
                    </button>
                  </template>
                  <!-- Own scrollbar (phones hide the native one until you scroll);
                       the native bar is hidden so there is only one -->
                  <b-dropdown-item
                    custom
                    paddingless
                    class="dd-scroll-holder"
                    aria-role="listitem"
                  >
                    <div
                      v-if="ddScroll.mobileRegionDd"
                      class="dd-scroll-track"
                      :style="{ height: ddScroll.mobileRegionDd.track + 'px' }"
                    >
                      <div
                        class="dd-scroll-thumb"
                        :style="{ height: ddScroll.mobileRegionDd.thumb + 'px', transform: 'translateY(' + ddScroll.mobileRegionDd.top + 'px)' }"
                      />
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-for="r in regionOptions"
                    :key="'fr' + r.id"
                    :value="r.id"
                    aria-role="listitem"
                  >
                    <span class="multi-select-option">
                      <span>{{ r.name.capitalize() }} <span class="has-text-grey">({{ r.count }})</span></span>
                      <b-icon
                        :icon="parameters.filter.region_id.includes(r.id) ? 'checkbox-marked' : 'checkbox-blank-outline'"
                        size="is-small"
                        class="multi-select-check"
                      />
                    </span>
                  </b-dropdown-item>
                  <!-- Mobile: explicit close, like the Filtres panel's Terminé -->
                  <b-dropdown-item
                    custom
                    paddingless
                    class="multi-select-done"
                    aria-role="listitem"
                  >
                    <button
                      type="button"
                      class="multi-select-done-btn"
                      @click="$refs.mobileRegionDd.toggle()"
                    >
                      {{ $t("done") }}
                    </button>
                  </b-dropdown-item>
                </b-dropdown>
              </div>

              <div class="field is-hidden-tablet">
                <label class="label filter-label">
                  {{ $t("tab_city").capitalize() }}
                  <span class="multi-select-hint">({{ $t("selection_multiple") }})</span>
                </label>
                <b-dropdown
                  ref="mobileCityDd"
                  @active-change="onMobileDropdownActive($event, 'mobileCityDd')"
                  v-model="parameters.filter.city"
                  class="multi-select-dropdown"
                  multiple
                  expanded
                  scrollable
                  max-height="300"
                  aria-role="list"
                >
                  <template #trigger>
                    <button
                      class="button is-fullwidth is-justify-content-space-between multi-select-trigger"
                      type="button"
                    >
                      <span class="multi-select-label">{{ cityLabel }}</span>
                      <b-icon icon="menu-down" />
                    </button>
                  </template>
                  <!-- Own scrollbar (phones hide the native one until you scroll);
                       the native bar is hidden so there is only one -->
                  <b-dropdown-item
                    custom
                    paddingless
                    class="dd-scroll-holder"
                    aria-role="listitem"
                  >
                    <div
                      v-if="ddScroll.mobileCityDd"
                      class="dd-scroll-track"
                      :style="{ height: ddScroll.mobileCityDd.track + 'px' }"
                    >
                      <div
                        class="dd-scroll-thumb"
                        :style="{ height: ddScroll.mobileCityDd.thumb + 'px', transform: 'translateY(' + ddScroll.mobileCityDd.top + 'px)' }"
                      />
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-for="c in cityOptions"
                    :key="'fc' + c.name"
                    :value="c.name"
                    aria-role="listitem"
                  >
                    <span class="multi-select-option">
                      <span>{{ c.name }} <span class="has-text-grey">({{ c.count }})</span></span>
                      <b-icon
                        :icon="parameters.filter.city.includes(c.name) ? 'checkbox-marked' : 'checkbox-blank-outline'"
                        size="is-small"
                        class="multi-select-check"
                      />
                    </span>
                  </b-dropdown-item>
                  <!-- Mobile: explicit close, like the Filtres panel's Terminé -->
                  <b-dropdown-item
                    custom
                    paddingless
                    class="multi-select-done"
                    aria-role="listitem"
                  >
                    <button
                      type="button"
                      class="multi-select-done-btn"
                      @click="$refs.mobileCityDd.toggle()"
                    >
                      {{ $t("done") }}
                    </button>
                  </b-dropdown-item>
                </b-dropdown>
              </div>

              <!-- Price -->
              <div class="field">
                <label class="label filter-label">{{ $t("rent").capitalize() }}</label>
                <div class="px-2">
                  <b-slider
                    v-model="priceRange"
                    :min="0"
                    :max="priceCeiling"
                    :step="25"
                    lazy
                    rounded
                  >
                  </b-slider>
                </div>
                <p class="is-size-7 has-text-grey has-text-centered">
                  {{ priceRange[0] }}$ – {{ priceRange[1] }}$<span v-if="priceRange[1] >= priceCeiling">+</span>
                </p>
              </div>

              <!-- Square feet -->
              <div class="field">
                <label class="label filter-label">{{ $t("filter_square_feet").capitalize() }}</label>
                <div class="px-2">
                  <b-slider
                    v-model="squareFeetRange"
                    :min="0"
                    :max="squareFeetCeiling"
                    :step="25"
                    lazy
                    rounded
                  >
                  </b-slider>
                </div>
                <p class="is-size-7 has-text-grey has-text-centered">
                  {{ squareFeetRange[0] }} – {{ squareFeetRange[1] }} pi²<span v-if="squareFeetRange[1] >= squareFeetCeiling">+</span>
                </p>
              </div>

              <!-- Move-in date -->
              <div class="field">
                <label class="label filter-label">{{ $t("availability_move_in").capitalize() }}</label>
                <div class="availability-modes">
                  <b-radio v-model="availabilityMode" native-value="flexible">
                    {{ $t("availability_mode_flexible") }}
                  </b-radio>
                  <b-radio v-model="availabilityMode" native-value="fixed">
                    {{ $t("availability_mode_fixed") }}
                  </b-radio>
                  <b-radio v-model="availabilityMode" native-value="next30">
                    {{ $t("availability_next_30_days") }}
                  </b-radio>
                </div>

                <!-- Fixed: pick a move-in date; shows homes available on or
                     before that date -->
                <b-datepicker
                  v-if="availabilityMode === 'fixed'"
                  v-model="availabilityFixed"
                  expanded
                  icon="calendar-today"
                  :min-date="minMoveInDate"
                  :placeholder="$t('availability_pick_date')"
                  :mobile-native="false"
                />
              </div>

              <!-- Animals -->
              <div class="filter-section">
                <p class="filter-label">
                  {{ $t("filter_animals") }}
                </p>
                <b-checkbox
                  v-model="parameters.filter.cat_tolerance"
                  :true-value="true"
                  :false-value="null"
                >
                  {{ $t("cats_accepted") }}
                </b-checkbox>
                <b-checkbox
                  v-model="parameters.filter.dog_tolerance"
                  :true-value="true"
                  :false-value="null"
                >
                  {{ $t("dogs_accepted") }}
                </b-checkbox>
              </div>

              <!-- Inclusions -->
              <div class="filter-section">
                <p class="filter-label">
                  {{ $t("inclusions") }}
                </p>
                <b-checkbox
                  v-model="parameters.filter.appliances"
                  :true-value="true"
                  :false-value="null"
                >
                  {{ $t("filter_appliances") }}
                </b-checkbox>
                <b-checkbox
                  v-model="parameters.filter.inclusions"
                  native-value="internet"
                >
                  {{ $t("filter_internet") }}
                </b-checkbox>
                <b-checkbox
                  v-model="parameters.filter.inclusions"
                  native-value="electricity"
                >
                  {{ $t("filter_electricity") }}
                </b-checkbox>
                <b-checkbox
                  v-model="parameters.filter.inclusions"
                  native-value="heater"
                >
                  {{ $t("filter_heating") }}
                </b-checkbox>
                <b-checkbox
                  v-model="parameters.filter.inclusions"
                  native-value="hotWater"
                >
                  {{ $t("filter_hot_water") }}
                </b-checkbox>
                <b-checkbox
                  v-model="parameters.filter.inclusions"
                  native-value="airConditioning"
                >
                  {{ $t("filter_air_conditioning") }}
                </b-checkbox>
                <b-checkbox
                  v-model="parameters.filter.inclusions"
                  native-value="heatPomp"
                >
                  {{ $t("filter_heat_pump") }}
                </b-checkbox>
              </div>

              <!-- Parking -->
              <div class="filter-section">
                <p class="filter-label">
                  {{ $t("filter_parking") }}
                </p>
                <b-checkbox
                  v-model="parameters.filter.inclusions"
                  native-value="indoorParking"
                >
                  {{ $t("filter_indoor_parking") }}
                </b-checkbox>
                <b-checkbox
                  v-model="parameters.filter.inclusions"
                  native-value="outdoorParking"
                >
                  {{ $t("filter_outdoor_parking") }}
                </b-checkbox>
              </div>

              <!-- Others -->
              <div class="filter-section">
                <p class="filter-label">
                  {{ $t("filter_others") }}
                </p>
                <b-checkbox
                  v-model="parameters.filter.has_washer_and_dryer_entry"
                  :true-value="true"
                  :false-value="null"
                >
                  {{ $t("filter_in_unit_laundry") }}
                </b-checkbox>
                <b-checkbox
                  v-model="parameters.filter.has_laundry_room"
                  :true-value="true"
                  :false-value="null"
                >
                  {{ $t("filter_building_laundry") }}
                </b-checkbox>

              </div>

              <!-- Reset every filter back to its default -->
              <b-button
                expanded
                icon-left="refresh"
                class="mt-4"
                @click="resetFilters"
              >
                {{ $t("reset_filters") }}
              </b-button>

              <!-- Mobile: pinned close, same pattern as the search panel -->
              <button
                type="button"
                class="search-apply-btn is-hidden-tablet filters-done-btn"
                @click="showMobileFilters = false"
              >
                {{ $t("done") }}
              </button>
            </aside>

            <!-- Results -->
            <div class="column">
          <!-- Mobile: the Liste|Carte toggle lives here, AFTER the filter
               panel, so open filters appear between Trier|Filtres and it -->
          <div class="view-toggle is-hidden-tablet mb-3">
            <button
              type="button"
              class="view-toggle-btn"
              :class="{ 'is-active': viewMode === 'list' }"
              @click="viewMode = 'list'"
            >
              <b-icon
                icon="view-list"
                size="is-small"
              />
              <span>{{ $t("view_list").capitalize() }}</span>
            </button>
            <button
              type="button"
              class="view-toggle-btn"
              :class="{ 'is-active': viewMode === 'map' }"
              @click="viewMode = 'map'"
            >
              <b-icon
                icon="map-outline"
                size="is-small"
              />
              <span>{{ $t("view_map").capitalize() }}</span>
            </button>
          </div>

          <!-- Map view (Airbnb-like) -->
          <div
            v-show="viewMode === 'map'"
            ref="mapEl"
            class="map-view"
          />

          <!-- Loading spinner -->
          <div
            v-if="loading && viewMode === 'list'"
            class="results-loader"
          >
            <b-icon
              icon="loading"
              size="is-large"
              class="results-loader-icon"
            />
            <p class="mt-3 has-text-grey is-size-6">
              {{ $t("loading").capitalize() }}…
            </p>
          </div>
          <div
            v-if="!loading && viewMode === 'list'"
            class="columns mb-6 is-flex-wrap-wrap results-grid"
          >
            <div
              v-for="(property, key) in properties"
              :key="key"
              class="column is-one-third-widescreen is-half-tablet"
            >
              <div class="card">
                <div class="card-image-wrap">
                  <!-- Red promotion banner overlaid on the image so promo and
                       non-promo cards keep the same height -->
                  <div
                    v-if="property.promotion_details"
                    class="promotion-banner"
                  >
                    {{ promotionBanner(property.promotion_details) }}
                  </div>
                  <!-- Published in the last 30 days (promotion wins if both) -->
                  <div
                    v-else-if="property.is_new"
                    class="promotion-banner banner-new"
                  >
                    {{ $t("banner_new") }}
                  </div>
                  <d-slider
                    :data="cardMedia(property.media)"
                    :clickable-images="!isMobileView"
                    @image-click="$router.push('/' + property.slug)"
                  />
                </div>

                <div
                  class="card-content"
                  style="padding: 1rem; padding-bottom: 1.3rem"
                >
                  <component
                    :is="isMobileView ? 'div' : 'router-link'"
                    :to="isMobileView ? undefined : '/' + property.slug"
                  >
                    <!-- Price (monthly-credit promo shows regular struck + discounted) -->
                    <p class="title has-text-weight-medium is-4 mb-0">
                      <template
                        v-if="property.promotion_details && property.promotion_details.display_price < property.promotion_details.regular_price"
                      >
                        <span class="regular-price-struck">{{ property.promotion_details.regular_price }}$</span>
                        <span>{{ property.promotion_details.display_price }}$</span>
                      </template>
                      <span v-else-if="property.property_status_asked_rent">
                        {{ property.property_status_asked_rent }}$
                      </span>
                    </p>

                    <!-- Address -->
                    <p style="line-clamp: 1; margin-bottom: 18px">
                      {{ $shortStreet(property.address.address_1)
                      }}{{ property.address.address_2 ? ` #${property.address.address_2}` : "" }},
                      {{ property.address.city }}
                    </p>


                    <!-- Features -->

                    <div class="features">
                      <div class="feature-item">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          class="feature-icon"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M9.638 1.093a.75.75 0 01.724 0l2 1.104a.75.75 0 11-.724 1.313L10 2.607l-1.638.903a.75.75 0 11-.724-1.313l2-1.104zM5.403 4.287a.75.75 0 01-.295 1.019l-.805.444.805.444a.75.75 0 01-.724 1.314L3.5 7.02v.73a.75.75 0 01-1.5 0v-2a.75.75 0 01.388-.657l1.996-1.1a.75.75 0 011.019.294zm9.194 0a.75.75 0 011.02-.295l1.995 1.101A.75.75 0 0118 5.75v2a.75.75 0 01-1.5 0v-.73l-.884.488a.75.75 0 11-.724-1.314l.806-.444-.806-.444a.75.75 0 01-.295-1.02zM7.343 8.284a.75.75 0 011.02-.294L10 8.893l1.638-.903a.75.75 0 11.724 1.313l-1.612.89v1.557a.75.75 0 01-1.5 0v-1.557l-1.612-.89a.75.75 0 01-.295-1.019zM2.75 11.5a.75.75 0 01.75.75v1.557l1.608.887a.75.75 0 01-.724 1.314l-1.996-1.101A.75.75 0 012 14.25v-2a.75.75 0 01.75-.75zm14.5 0a.75.75 0 01.75.75v2a.75.75 0 01-.388.657l-1.996 1.1a.75.75 0 11-.724-1.313l1.608-.887V12.25a.75.75 0 01.75-.75zm-7.25 4a.75.75 0 01.75.75v.73l.888-.49a.75.75 0 01.724 1.313l-2 1.104a.75.75 0 01-.724 0l-2-1.104a.75.75 0 11.724-1.313l.888.49v-.73a.75.75 0 01.75-.75z"
                            clip-rule="evenodd"
                          />
                        </svg>
                        <span>
                          {{ $t(property.type).capitalize() }}
                        </span>

                        <!-- Distance from the visitor, pushed to the right
                             (near-me only) -->
                        <div
                          v-if="property.distance_km != null"
                          class="feature-item feature-item-right"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            class="feature-icon"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 001.03.572l.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z"
                              clip-rule="evenodd"
                            />
                          </svg>
                          <span>{{ property.distance_km }} km</span>
                        </div>
                      </div>

                      <div class="feature-item">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          class="feature-icon"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z"
                            clip-rule="evenodd"
                          />
                        </svg>
                        <span v-if="!property.is_available">
                          {{ $dayjs(property.availability_date).utc().format("LL") }}
                        </span>
                        <span v-if="property.is_available">
                          {{ $t("from_now").capitalize() }}
                        </span>
                      </div>

                      <!-- Rent agents (avatar + name) -->
                      <div
                        v-if="property.latest_status && property.latest_status.rent_agents && property.latest_status.rent_agents.length"
                        class="card-agents"
                      >
                        <span
                          v-for="agent in property.latest_status.rent_agents"
                          :key="agent.id"
                          class="card-agent card-agent-link"
                          :title="$t('see_agent_listings')"
                          @click.stop.prevent="selectAgent(agent)"
                        >
                          <img
                            class="card-agent-avatar"
                            :src="agent.avatar_url"
                            :alt="agent.name"
                          >
                          <span>{{ agent.name }}</span>
                        </span>
                      </div>
                    </div>
                  </component>
                </div>
                <!-- button small (desktop only) -->
                <div class="button-small-wrapper is-hidden-mobile">
                  <div
                    class="button-small button-small-position"
                    style="cursor: pointer"
                    @click="showModalInclusion(property.active_inclusions)"
                  >
                    <span class="icon">
                      <i class="mdi mdi-check-circle" />
                    </span>
                    <span>
                      {{ $t("inclusions").capitalize() }} ({{
                        property.inclusions_count
                      }})
                    </span>
                  </div>

                  <div
                    v-if="property.promotion_details"
                    class="button-small button-small-position"
                    style="cursor: pointer"
                    @click="showModalPromotion(property.promotion_details)"
                  >
                    <span class="icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        style="width: 1.1em; height: 1.1em"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M5.5 3A2.5 2.5 0 003 5.5v2.879a2.5 2.5 0 00.732 1.767l6.5 6.5a2.5 2.5 0 003.536 0l2.878-2.878a2.5 2.5 0 000-3.536l-6.5-6.5A2.5 2.5 0 008.38 3H5.5zM6 7a1 1 0 100-2 1 1 0 000 2z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </span>
                    <span>
                      {{ $t("promotion").capitalize() }}
                    </span>
                  </div>
                </div>

                <!-- button cta -->
                <button
                  class="button button-more-info is-hidden-tablet"
                  @click="$router.push('/' + property.slug)"
                >
                  {{ $t("more_info").capitalize() }}
                </button>
                <button
                  class="button is-dark is-fullwidth button-cta"
                  @click="openContactModal(property.id)"
                >
                  {{ $t("contact_agent") }}
                </button>
              </div>
            </div>
            <contact-agents-modal
              v-model="isShownModalContact"
              :property-id="contactPropertyId"
            />
            <b-modal
              v-model="isShownModalInclusion"
              :width="600"
              @close="closeModalInclusion()"
            >
              <div class="modal-card">
                <header
                  class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start is-size-6 has-text-weight-bold"
                >
                  {{ $t("inclusion_list").capitalize() }}
                </header>
                <section class="modal-card-body">
                  <v-inclusions
                    v-if="inclusions.length"
                    :inclusions="inclusions"
                    class="is-button-block-responsive"
                  />
                </section>
                <footer class="modal-card-foot">
                  <b-button
                    :label="$t('close')"
                    @click="closeModalInclusion()"
                  />
                </footer>
              </div>
            </b-modal>

            <!-- Promotion details modal (opened from the red promotion button) -->
            <b-modal
              v-model="isShownModalPromotion"
              :width="600"
              @close="closeModalPromotion()"
            >
              <div class="modal-card">
                <header
                  class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start is-size-6 has-text-weight-bold"
                >
                  {{ $t("promotion").capitalize() }}
                </header>
                <section
                  v-if="promotionModal"
                  class="modal-card-body"
                >
                  <template v-if="promotionModal.type === 'monthly_credit'">
                    <p class="mb-2">
                      {{ $t("promotion_credit_tooltip_line1", { price: promotionModal.display_price }) }}
                    </p>
                    <p>
                      {{ $t("promotion_credit_tooltip_line2", { price: promotionModal.regular_price }) }}
                    </p>
                  </template>
                  <p v-else-if="promotionModal.type === 'free_month'">
                    {{ $t("promotion_free_month_tooltip") }}
                  </p>
                  <p v-else>
                    {{ promotionModal.text }}
                  </p>
                </section>
                <footer class="modal-card-foot">
                  <b-button
                    :label="$t('close')"
                    @click="closeModalPromotion()"
                  />
                </footer>
              </div>
            </b-modal>
          </div>
          <div
            v-if="!loading && viewMode === 'list'"
            class="pagination"
          >
            <b-pagination
              v-model="parameters.page"
              :total="pagination.total"
              :current:page="pagination.current_page"
              :per-page="pagination.per_page"
              :is-rounded="true"
              size="is-small"
              :range-before="3"
              order="is-centered"
              :range-after="3"
            />
          </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-layout>
</template>
<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Map view with no filter: open on this radius around the visitor (metres;
// with the map's ~570px height this lands on Leaflet zoom 13, ±3 km)
const MAP_NEARBY_RADIUS = 3000;
// ...widened to the nearest pin when none is inside, unless it's this far
const MAP_NEARBY_MAX_EXTEND = 25000;
import ShowInclusionPopupComponent from "./components/ShowInclusionPopupComponent.vue";

// "Ulrika Faber" -> "ulrika-faber" (readable ?agent= URLs)
function agentSlug(name) {
  return (name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
import ContactAgentsModal from "./components/ContactAgentsModal.vue";

export default {
  components: {
    "v-inclusions": ShowInclusionPopupComponent,
    "contact-agents-modal": ContactAgentsModal,
  },
  metaInfo() {
    return {
      title: this.$t("public_page_title").capitalize(),
    };
  },
  data() {
    // Restore the whole listing state from the URL so filters, search, sort and
    // page survive a back navigation / refresh / shared link.
    const q = this.$route.query;
    const csv = (v) => (typeof v === "string" && v.length ? v.split(",") : []);
    const range = (v, def) => {
      const m = typeof v === "string" ? v.match(/^(\d+)-(\d+)$/) : null;
      return m ? [Number(m[1]), Number(m[2])] : def.slice();
    };

    const priceRange = range(q.pr, [0, 2500]);
    const squareFeetRange = range(q.sf, [0, 1500]);
    const availabilityMode = ["fixed", "next30"].includes(q.am)
      ? q.am
      : "flexible";
    const availabilityFixed =
      availabilityMode === "fixed" && typeof q.ad === "string" && q.ad
        ? this.$dayjs(q.ad).toDate()
        : null;

    let availabilityDate = null;
    if (availabilityMode === "next30") {
      availabilityDate = this.$dayjs().add(30, "day").format("YYYY-MM-DD");
    } else if (availabilityMode === "fixed" && availabilityFixed) {
      availabilityDate = this.$dayjs(availabilityFixed).format("YYYY-MM-DD");
    }

    const sortMode = ["distance", "price_asc", "price_desc"].includes(q.s)
      ? q.s
      : "recent";

    return {
      loading: true,
      properties: [],
      inclusions: [],
      employee: null,
      owner: null,
      parameters: {
        page: parseInt(q.p, 10) > 1 ? parseInt(q.p, 10) : 1,
        search: typeof q.q === "string" && q.q ? q.q : null,
        near: null,
        sort: ["price_asc", "price_desc"].includes(sortMode) ? sortMode : null,
        filter: {
          type: csv(q.t),
          region_id: csv(q.r).map(Number),
          city: csv(q.city),
          rent_agent_id: (() => {
            const slugs = csv(q.agent);
            return (this.$laravel.publicRentAgents || [])
              .filter((a) => slugs.includes(agentSlug(a.name)))
              .map((a) => a.id);
          })(),
          price: {
            min: priceRange[0] > 0 ? priceRange[0] : null,
            max: priceRange[1] < 2500 ? priceRange[1] : null,
          },
          cat_tolerance: q.cat === "1" ? true : null,
          dog_tolerance: q.dog === "1" ? true : null,
          appliances: q.app === "1" ? true : null,
          inclusions: csv(q.inc),
          has_washer_and_dryer_entry: q.wd === "1" ? true : null,
          has_laundry_room: q.lr === "1" ? true : null,
          square_feet: {
            min: squareFeetRange[0] > 0 ? squareFeetRange[0] : null,
            max: squareFeetRange[1] < 1500 ? squareFeetRange[1] : null,
          },
          availability_date: availabilityDate,
        },
      },
      squareFeetCeiling: 1500,
      squareFeetRange,
      priceCeiling: 2500,
      priceRange,
      availabilityMode,
      availabilityFixed,
      nearLoading: false,
      // [lat, lng] once the visitor granted geolocation (drives the map's
      // initial viewport)
      userPosition: null,
      searchText: typeof q.q === "string" && q.q ? q.q : null,
      viewMode: q.view === "map" ? "map" : "list",
      regionOptions: [],
      cityOptions: [],
      addressSuggestions: [],
      // Opens on the Quartier list; typing switches to "Tous" (all categories)
      // Mobile has no tabs: the search bar is an address input
      searchTab: window.matchMedia("(max-width: 768px)").matches ? "address" : "region",
      searchFocused: false,
      // Panel visibility, decoupled from input focus: on mobile the panel
      // stays open with the keyboard dismissed while browsing quartiers
      suggestionsOpen: false,
      // Suggestion panel sizing, computed from the visual viewport so the
      // panel never extends under the mobile on-screen keyboard: desktop
      // caps max-height, mobile pins the panel from the input to bottom: 0
      suggestPanelMax: null,
      suggestPanelFixedTop: null,
      suggestPanelFixedHeight: null,
      // Mobile: card tap doesn't navigate; a "Plus d'infos" button does
      isMobileView: window.matchMedia("(max-width: 768px)").matches,
      sortMode,
      showMobileFilters: false,
      // Mobile dropdown scroll indicators, keyed by dropdown ref
      ddScroll: {},
      lastRequestId: 0,
      isShownModalContact: false,
      contactPropertyId: null,
      isShownModalInclusion: false,
      isShownModalPromotion: false,
      promotionModal: null,
      pagination: {
        current_page: null,
        page: null,
        per_page: null,
        last_page: null,
        total:null,
        links: []
      },
    };
  },
  watch: {
    // Back to page 1 whenever a filter or the search term changes
    "parameters.filter": {
      handler() {
        this.parameters.page = 1;
      },
      deep: true,
    },
    "parameters.search"() {
      this.parameters.page = 1;
    },
    searchText(value) {
      // Desktop: typing always searches the three categories at once; the
      // specific tabs are a momentary refinement. Mobile has no "Tous" tab
      // (typing only happens on Adresse).
      if (value && !this.isMobileView && this.searchTab !== "all") {
        this.searchTab = "all";
      }
      this.maybeFetchAddresses();
      // Mobile: the search runs by itself while typing (street or civic
      // number), no Enter and no panel
      if (this.isMobileView) {
        this.debouncedApplySearch();
      }
      // Emptying the box clears the applied text search
      if (!value && this.parameters.search) {
        this.parameters.search = null;
      }
    },
    searchTab() {
      this.maybeFetchAddresses();
    },
    // While the panel is open, track the visual viewport (the on-screen
    // keyboard shrinks it) and size the panel to the space actually visible
    suggestionsOpen(open) {
      const vv = window.visualViewport;

      if (open) {
        this.$nextTick(this.updateSuggestPanelMax);
        if (vv) {
          vv.addEventListener("resize", this.updateSuggestPanelMax);
          vv.addEventListener("scroll", this.updateSuggestPanelMax);
        }
        window.addEventListener("scroll", this.updateSuggestPanelMax, true);
        if (this.isMobileView) {
          // Closing is an explicit outside tap on mobile (blur only hides
          // the keyboard)
          document.addEventListener("touchstart", this.onDocTouch, { capture: true, passive: true });
          // Once the keyboard starts opening, bring the search bar to the
          // top (just under the fixed navbar) so the quartier list gets
          // the whole space left above the keyboard
          setTimeout(() => {
            const field = this.$refs.searchSuggestField;

            if (field && this.suggestionsOpen) {
              const y = window.scrollY + field.getBoundingClientRect().top - 64;

              window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
            }
          }, 300);
          // Once positioned, freeze the page behind the panel: short tabs
          // (Ville/Adresse) don't overflow, so a drag would scroll the
          // listing underneath instead
          setTimeout(() => this.syncScrollLock(), 850);
        }
      } else {
        this.syncScrollLock();
        if (vv) {
          vv.removeEventListener("resize", this.updateSuggestPanelMax);
          vv.removeEventListener("scroll", this.updateSuggestPanelMax);
        }
        window.removeEventListener("scroll", this.updateSuggestPanelMax, true);
        document.removeEventListener("touchstart", this.onDocTouch, { capture: true });
        this.suggestPanelMax = null;
        this.suggestPanelFixedTop = null;
      }
    },
    // Mobile filter panel opens as a frozen overlay (same as search)
    showMobileFilters() {
      this.syncScrollLock();
    },
    // Entering/leaving an agent page rescopes the suggestion counts
    "parameters.filter.rent_agent_id"() {
      this.fetchRegionOptions();
      this.maybeFetchAddresses();
    },
    viewMode(mode) {
      this.syncQuery();
      if (mode === "map") {
        this.$nextTick(() => {
          this.initMap();
          this.fetchMapItems();
        });
      }
    },
    "parameters.near"() {
      this.parameters.page = 1;
    },
    // Sort dropdown: distance re-uses the geolocation flow; price sorts go
    // through the `sort` query param.
    sortMode(value) {
      if (value === "distance") {
        this.parameters.sort = null;
        if (!this.parameters.near) {
          this.enableNearMe();
        }
      } else {
        this.parameters.near = null;
        this.parameters.sort = value === "recent" ? null : value;
      }
    },
    parameters: {
      handler() {
        this.debouncedLoadData();
      },
      deep: true,
    },
    // Map the slider ranges onto their filters (bounds mean "no limit")
    squareFeetRange(value) {
      this.parameters.filter.square_feet = {
        min: value[0] > 0 ? value[0] : null,
        max: value[1] < this.squareFeetCeiling ? value[1] : null,
      };
    },
    priceRange(value) {
      this.parameters.filter.price = {
        min: value[0] > 0 ? value[0] : null,
        max: value[1] < this.priceCeiling ? value[1] : null,
      };
    },
    // Move-in date filter — 3 modes: flexible / fixed (calendar) / next 30 days
    availabilityMode() {
      // Reset the date whenever the mode changes
      this.availabilityFixed = null;
      this.applyAvailabilityFilter();
    },
    availabilityFixed() {
      this.applyAvailabilityFilter();
    },
  },
  beforeDestroy() {
    const vv = window.visualViewport;

    if (vv) {
      vv.removeEventListener("resize", this.updateSuggestPanelMax);
      vv.removeEventListener("scroll", this.updateSuggestPanelMax);
    }
    window.removeEventListener("scroll", this.updateSuggestPanelMax, true);
    document.removeEventListener("touchstart", this.onDocTouch, { capture: true });
    document.documentElement.classList.remove("suggest-scroll-lock");
  },
  created() {
    this.debouncedLoadData = _.debounce(this.loadData, 400);
    this.loadData();
    this.debouncedFetchAddresses = _.debounce(this.fetchAddresses, 300);
    this.debouncedApplySearch = _.debounce(() => {
      this.parameters.search = this.searchText || null;
    }, 450);
    if (this.viewMode === "map") {
      this.$nextTick(() => {
        this.initMap();
        this.fetchMapItems();
      });
    }
    this.fetchRegionOptions();
    // Ask for the visitor's location by default; silent so declining is quiet.
    // Skip it when the URL pins a price sort, so we don't override it with the
    // automatic distance sort.
    if (this.sortMode !== "price_asc" && this.sortMode !== "price_desc") {
      this.enableNearMe(true);
    }
  },
  computed: {
    // True when the visitor narrowed the listing (search text or any filter);
    // sort, page, view and the agent page scoping don't count
    hasActiveFilters() {
      const f = this.parameters.filter;
      return Boolean(
        this.parameters.search ||
          f.type.length ||
          f.region_id.length ||
          f.city.length ||
          f.inclusions.length ||
          f.price.min !== null ||
          f.price.max !== null ||
          f.square_feet.min !== null ||
          f.square_feet.max !== null ||
          f.cat_tolerance ||
          f.dog_tolerance ||
          f.appliances ||
          f.has_washer_and_dryer_entry ||
          f.has_laundry_room ||
          this.availabilityMode !== "flexible"
      );
    },
    // Public Type options: "Chambre" first, and the 6.5/7.5/8.5/9.5 sizes
    // collapsed into a single "6.5 et +" pseudo-type (expanded in allParameters).
    publicPropertyTypes() {
      const collapsed = ["TYPE_6_5", "TYPE_7_5", "TYPE_8_5", "TYPE_9_5"];
      const base = this.$laravel.propertyTypes.filter(
        (t) => t !== "TYPE_ROOM" && !collapsed.includes(t)
      );

      return ["TYPE_ROOM", ...base, "TYPE_6_5_PLUS"];
    },
    // Trigger labels: single selection shows its name, several show a count
    // ("4 sélections") instead of a truncated comma list.
    typeLabel() {
      const selected = this.parameters.filter.type;

      if (!selected.length) {
        return this.$t("all").capitalize();
      }
      if (selected.length === 1) {
        return this.$t(selected[0]);
      }

      return this.$t("selections_count", { count: selected.length });
    },
    // Mobile Filtres panel: trigger labels of the Quartier / Ville selects
    regionLabel() {
      const ids = this.parameters.filter.region_id;

      if (!ids.length) {
        return this.$t("all").trim().capitalize();
      }
      if (ids.length === 1) {
        const r = this.regionOptions.find((o) => o.id === ids[0]) || this.selectedRegions[0];
        return r ? r.name.capitalize() : this.$t("selections_count", { count: 1 });
      }

      return this.$t("selections_count", { count: ids.length });
    },
    cityLabel() {
      const cities = this.parameters.filter.city;

      if (!cities.length) {
        return this.$t("all").trim().capitalize();
      }

      return cities.length === 1 ? cities[0] : this.$t("selections_count", { count: cities.length });
    },
    searchTabs() {
      const tabs = [
        { key: "all", label: this.$t("tab_all").capitalize() },
        { key: "region", label: this.$t("tab_region").capitalize() },
        { key: "city", label: this.$t("tab_city").capitalize() },
        { key: "address", label: this.$t("tab_address").capitalize() },
      ];

      // Mobile: no tabs at all — the bar only searches addresses (quartiers
      // and villes live in the Filtres panel)
      return this.isMobileView ? tabs.filter((t) => t.key === "address") : tabs;
    },
    // "Tous" tab: the three categories together, capped so the panel stays
    // scannable (the dedicated tabs show the full lists)
    allTabRegionSuggestions() {
      return this.regionSuggestions.slice(0, 5);
    },
    allTabCitySuggestions() {
      return this.citySuggestions.slice(0, 5);
    },
    allTabAddressSuggestions() {
      return this.addressSuggestions.slice(0, 5);
    },
    citySuggestions() {
      const norm = (v) =>
        (v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      // Mobile never types to filter these lists (typing = Adresse only)
      const q = this.isMobileView ? "" : norm(this.searchText);

      return this.cityOptions.filter((c) => !q || norm(c.name).includes(q));
    },
    // Regions matching the typed text (accent-insensitive); all when empty
    regionSuggestions() {
      const norm = (v) =>
        (v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      const q = this.isMobileView ? "" : norm(this.searchText);

      return this.regionOptions.filter((r) => !q || norm(r.name).includes(q));
    },
    selectedRegions() {
      return (this.$laravel.propertyRegions || []).filter((r) =>
        this.parameters.filter.region_id.includes(r.id)
      );
    },
    selectedAgents() {
      return (this.$laravel.publicRentAgents || []).filter((a) =>
        this.parameters.filter.rent_agent_id.includes(a.id)
      );
    },
    selectedAgentNames() {
      return (this.$laravel.publicRentAgents || [])
        .filter((a) => this.parameters.filter.rent_agent_id.includes(a.id))
        .map((a) => a.name);
    },
    heroTitle() {
      return this.$t("public_page_title").capitalize();
    },
    // Exactly one agent selected -> their identity takes over the hero
    heroAgent() {
      return this.selectedAgents.length === 1 ? this.selectedAgents[0] : null;
    },
    heroKicker() {
      return this.$t("public_page_subtitle");
    },
    suggestPanelStyle() {
      // Mobile: the panel may grow down to the keyboard (bottom = keyboard
      // top with interactive-widget=resizes-content) so its internal scroll
      // reaches the end, but it hugs its content: it ends right after the
      // Rechercher button instead of leaving a white block below it
      if (this.suggestPanelFixedTop !== null) {
        return {
          position: "fixed",
          top: this.suggestPanelFixedTop + "px",
          left: "12px",
          right: "12px",
          maxHeight: this.suggestPanelFixedHeight + "px",
          zIndex: 45,
        };
      }

      return this.suggestPanelMax ? { maxHeight: this.suggestPanelMax + "px" } : {};
    },
    sortOptions() {
      return [
        { value: "recent", label: this.$t("sort_recent").capitalize() },
        { value: "distance", label: this.$t("near_me_active").capitalize() },
        { value: "price_asc", label: this.$t("sort_price_asc").capitalize() },
        { value: "price_desc", label: this.$t("sort_price_desc").capitalize() },
      ];
    },
    sortLabel() {
      const current = this.sortOptions.find((o) => o.value === this.sortMode);

      return current ? current.label : this.$t("sort_recent").capitalize();
    },
    // "Fixe" calendar can't go before today.
    minMoveInDate() {
      return this.$dayjs().startOf("day").toDate();
    },
  },
  methods: {
    // Translate the selected move-in mode into the filter payload the backend
    // already understands (scalar => "available on or before that date").
    applyAvailabilityFilter() {
      const filter = this.parameters.filter;

      if (this.availabilityMode === "next30") {
        filter.availability_date = this.$dayjs()
          .add(30, "day")
          .format("YYYY-MM-DD");
      } else if (this.availabilityMode === "fixed") {
        filter.availability_date = this.availabilityFixed
          ? this.$dayjs(this.availabilityFixed).format("YYYY-MM-DD")
          : null;
      } else {
        // Flexible: any move-in date
        filter.availability_date = null;
      }
    },
    // Ask the visitor for their location and sort listings by nearest.
    // `silent` (used for the automatic request on load) suppresses error
    // toasts so we don't nag visitors who decline.
    enableNearMe(silent = false) {
      if (!navigator.geolocation) {
        if (!silent) {
          this.$buefy.toast.open({
            message: this.$t("geolocation_unsupported"),
            type: "is-warning",
          });
        }
        return;
      }
      this.nearLoading = true;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.nearLoading = false;
          const { latitude, longitude } = position.coords;
          this.parameters.near = `${latitude},${longitude}`;
          this.userPosition = [latitude, longitude];
          this.fitMapViewport();
          // Reflect the active distance sort in the dropdown
          this.sortMode = "distance";
        },
        () => {
          this.nearLoading = false;
          if (this.sortMode === "distance") {
            this.sortMode = "recent";
          }
          if (!silent) {
            this.$buefy.toast.open({
              message: this.$t("geolocation_denied"),
              type: "is-danger",
            });
          }
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    },
    // Cards lead with the photos; videos come after (a listing with photos
    // always shows its first photo first)
    cardMedia(media) {
      const list = media || [];
      const isVideo = (m) => m.mime_type && m.mime_type.startsWith("video/");

      return [...list.filter((m) => !isVideo(m)), ...list.filter(isVideo)];
    },
    // Clicking an agent on a card shows that agent's listings
    selectAgent(agent) {
      this.parameters.filter.rent_agent_id = [agent.id];
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    removeAgent(id) {
      this.parameters.filter.rent_agent_id =
        this.parameters.filter.rent_agent_id.filter((x) => x !== id);
    },
    // "Voir tous les logements" on the agent hero: back to the full listing
    clearAgent() {
      this.parameters.filter.rent_agent_id = [];
    },
    // Everything back to defaults (the agent scope has its own hero button)
    resetFilters() {
      const filter = this.parameters.filter;

      filter.type = [];
      filter.region_id = [];
      filter.city = [];
      filter.price = { min: null, max: null };
      filter.cat_tolerance = null;
      filter.dog_tolerance = null;
      filter.appliances = null;
      filter.inclusions = [];
      filter.has_washer_and_dryer_entry = null;
      filter.has_laundry_room = null;
      filter.square_feet = { min: null, max: null };

      this.priceRange = [0, this.priceCeiling];
      this.squareFeetRange = [0, this.squareFeetCeiling];
      this.availabilityMode = "flexible";
      this.availabilityFixed = null;
      this.searchText = null;
      this.parameters.search = null;
      this.parameters.page = 1;
    },
    toggleCity(name) {
      const list = this.parameters.filter.city;
      const i = list.indexOf(name);

      if (i === -1) {
        list.push(name);
        if (this.parameters.search) {
          this.parameters.search = null;
        }
      } else {
        list.splice(i, 1);
      }
    },
    initMap() {
      if (this.mapInstance) {
        this.mapInstance.invalidateSize();
        return;
      }
      // Leaflet + Mapbox raster tiles: no WebGL requirement, works everywhere
      this.mapInstance = L.map(this.$refs.mapEl, {
        center: [45.53, -73.6],
        zoom: 10,
        scrollWheelZoom: true,
      });
      L.tileLayer(
        "https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=" +
          this.$laravel.mapboxPublicToken,
        {
          tileSize: 512,
          zoomOffset: -1,
          maxZoom: 19,
          attribution: "© Mapbox © OpenStreetMap",
        }
      ).addTo(this.mapInstance);
      this.mapMarkers = [];
    },
    fetchMapItems() {
      this.$http
        .get(this.$laravel.route("properties-list"), {
          params: { ...this.allParameters(), map: 1 },
        })
        .then((response) => {
          this.renderMapMarkers(response.data.data);
        });
    },
    renderMapMarkers(items) {
      if (!this.mapInstance) {
        return;
      }
      (this.mapMarkers || []).forEach((m) => m.remove());
      this.mapMarkers = [];

      // Several apartments in one building share the same coordinates: group
      // them so one pin represents the address (cheapest unit + "+N logements")
      // and the popup browses the units with ‹ › arrows.
      const groups = new Map();
      items.forEach((item) => {
        const key = Number(item.lat).toFixed(5) + "," + Number(item.lng).toFixed(5);
        if (!groups.has(key)) {
          groups.set(key, []);
        }
        groups.get(key).push(item);
      });

      const effectivePrice = (item) => Number(item.display_price || item.price) || 0;
      const bounds = [];

      groups.forEach((units) => {
        units.sort((a, b) => effectivePrice(a) - effectivePrice(b));
        const cheapest = units[0];

        // Pin shows the lowest promo/asked price of the address. All pins
        // are black (no red/blue by promotion or nouveauté); the popup card
        // still carries the banners.
        const pinClass = "map-price-pin";

        // Several units at the address: "1500$ (4)" = lowest rent, unit count
        let label = (cheapest.display_price || cheapest.price) + "$";
        if (units.length > 1) {
          label += ' <span class="map-pin-more">(' + units.length + ")</span>";
        }

        const icon = L.divIcon({
          className: "",
          html: '<div class="' + pinClass + '">' + label + "</div>",
          iconSize: null,
        });

        let index = 0;
        const popup = L.popup({ maxWidth: 260, className: "map-popup" })
          .setContent(this.mapPopupHtml(units, index));

        // The arrows live in Leaflet-injected HTML: (re)bind them every time
        // the content is rendered — on open and after each setContent
        const bindNav = () => {
          const el = popup.getElement();
          if (!el) {
            return;
          }
          const go = (delta) => (ev) => {
            L.DomEvent.stop(ev);
            index = (index + delta + units.length) % units.length;
            popup.setContent(this.mapPopupHtml(units, index));
            bindNav();
          };
          const prev = el.querySelector("[data-map-nav='prev']");
          const next = el.querySelector("[data-map-nav='next']");
          if (prev) {
            L.DomEvent.on(prev, "click", go(-1));
          }
          if (next) {
            L.DomEvent.on(next, "click", go(1));
          }
        };

        const marker = L.marker([cheapest.lat, cheapest.lng], { icon })
          .bindPopup(popup)
          .on("popupopen", bindNav)
          .addTo(this.mapInstance);

        this.mapMarkers.push(marker);
        bounds.push([cheapest.lat, cheapest.lng]);
      });

      this.mapPinLatLngs = bounds;
      this.fitMapViewport();
    },
    // Initial viewport. With no filter and a known visitor position: zoom on
    // MAP_NEARBY_RADIUS around them (widened to the nearest pin when that
    // area holds none) and mark their position. Otherwise fit every result.
    fitMapViewport() {
      const map = this.mapInstance;
      const pins = this.mapPinLatLngs || [];
      if (!map || this.viewMode !== "map") {
        return;
      }

      if (this.userPosition) {
        if (!this.userMarker) {
          this.userMarker = L.circleMarker(this.userPosition, {
            radius: 7,
            color: "#fff",
            weight: 2,
            fillColor: "#2563eb",
            fillOpacity: 1,
            interactive: false,
          }).addTo(map);
        }
        this.userMarker.setLatLng(this.userPosition);
      }

      if (this.userPosition && !this.hasActiveFilters) {
        const center = L.latLng(this.userPosition);
        let bounds = center.toBounds(MAP_NEARBY_RADIUS * 2);

        if (pins.length && !pins.some((p) => bounds.contains(p))) {
          const nearest = pins.reduce((best, p) =>
            center.distanceTo(p) < center.distanceTo(best) ? p : best
          );
          if (center.distanceTo(nearest) <= MAP_NEARBY_MAX_EXTEND) {
            bounds = bounds.extend(nearest);
          } else {
            bounds = null; // visitor is far from every listing: fit them all
          }
        }
        if (bounds) {
          map.fitBounds(bounds, { padding: [20, 20] });
          return;
        }
      }

      if (pins.length) {
        map.fitBounds(pins, { padding: [60, 60], maxZoom: 14 });
      }
    },
    // Popup for a pin: the card of the unit at `index`, plus a ‹ i / n ›
    // navigation bar when the address has several units
    mapPopupHtml(units, index) {
      let nav = "";
      if (units.length > 1) {
        nav =
          '<div class="map-popup-nav">' +
          '<button type="button" class="map-popup-nav-btn" data-map-nav="prev" aria-label="' +
          this.$t("map_popup_prev_unit") + '">&lsaquo;</button>' +
          '<span class="map-popup-nav-count">' +
          this.$t("map_popup_unit_count", { i: index + 1, n: units.length }) +
          "</span>" +
          '<button type="button" class="map-popup-nav-btn" data-map-nav="next" aria-label="' +
          this.$t("map_popup_next_unit") + '">&rsaquo;</button>' +
          "</div>";
      }
      return '<div class="map-popup-inner">' + this.mapPopupCardHtml(units[index]) + nav + "</div>";
    },
    mapPopupCardHtml(item) {
      const priceHtml = item.display_price
        ? '<span class="map-popup-struck">' + item.price + "$</span> " + item.display_price + "$"
        : item.price + "$";
      const availability = item.is_available
        ? this.$t("from_now").capitalize()
        : (item.availability_date ? this.$dayjs(item.availability_date).format("LL") : "");

      // Same banners as the list card: red promo wins over blue nouveauté
      let banner = "";
      if (item.promotion_type) {
        const label = item.promotion_type === "free_month"
          ? this.$t("promotion_free_month_banner")
          : this.$t("promotion").capitalize();
        banner = '<div class="map-popup-banner is-promo">' + label + "</div>";
      } else if (item.is_new) {
        banner = '<div class="map-popup-banner is-new">' + this.$t("banner_new") + "</div>";
      }

      const typeSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="map-popup-icon"><path fill-rule="evenodd" d="M9.638 1.093a.75.75 0 01.724 0l2 1.104a.75.75 0 11-.724 1.313L10 2.607l-1.638.903a.75.75 0 11-.724-1.313l2-1.104zM5.403 4.287a.75.75 0 01-.295 1.019l-.805.444.805.444a.75.75 0 01-.724 1.314L3.5 7.02v.73a.75.75 0 01-1.5 0v-2a.75.75 0 01.388-.657l1.996-1.1a.75.75 0 011.019.294zm9.194 0a.75.75 0 011.02-.295l1.995 1.101A.75.75 0 0118 5.75v2a.75.75 0 01-1.5 0v-.73l-.884.488a.75.75 0 11-.724-1.314l.806-.444-.806-.444a.75.75 0 01-.295-1.02zM7.343 8.284a.75.75 0 011.02-.294L10 8.893l1.638-.903a.75.75 0 11.724 1.313l-1.612.89v1.557a.75.75 0 01-1.5 0v-1.557l-1.612-.89a.75.75 0 01-.295-1.019zM2.75 11.5a.75.75 0 01.75.75v1.557l1.608.887a.75.75 0 01-.724 1.314l-1.996-1.101A.75.75 0 012 14.25v-2a.75.75 0 01.75-.75zm14.5 0a.75.75 0 01.75.75v2a.75.75 0 01-.388.657l-1.996 1.1a.75.75 0 11-.724-1.313l1.608-.887V12.25a.75.75 0 01.75-.75zm-7.25 4a.75.75 0 01.75.75v.73l.888-.49a.75.75 0 01.724 1.313l-2 1.104a.75.75 0 01-.724 0l-2-1.104a.75.75 0 11.724-1.313l.888.49v-.73a.75.75 0 01.75-.75z" clip-rule="evenodd"/></svg>';
      const calendarSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="map-popup-icon"><path fill-rule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z" clip-rule="evenodd"/></svg>';

      return (
        '<a class="map-popup-card" href="/public/' + item.slug + '">' +
        '<div class="map-popup-media">' + banner +
        (item.thumb ? '<img src="' + item.thumb + '" alt="">' : "") +
        "</div>" +
        '<div class="map-popup-body">' +
        '<p class="map-popup-price">' + priceHtml + "</p>" +
        '<p class="map-popup-address">' + item.address + "</p>" +
        '<div class="map-popup-row">' + typeSvg + "<span>" +
        this.$t(item.type).capitalize() + "</span></div>" +
        (availability
          ? '<div class="map-popup-row">' + calendarSvg + "<span>" + availability + "</span></div>"
          : "") +
        '<div class="map-popup-pastille"><span class="icon"><i class="mdi mdi-check-circle"></i></span><span>' +
        this.$t("inclusions").capitalize() + " (" + item.inclusions_count + ")</span></div>" +
        "</div></a>"
      );
    },
    // Enter applies the typed text to the listing (suggestions are live,
    // results are not)
    applySearch() {
      this.parameters.search = this.searchText || null;
      this.searchFocused = false;
      this.suggestionsOpen = false;
    },
    onSearchFocus() {
      this.searchFocused = true;
      // Mobile: no suggestion panel at all — the bar searches as you type
      if (!this.isMobileView) {
        this.suggestionsOpen = true;
      }
    },
    // Mobile: picking the Adresse tab hands the keyboard over (focus); the
    // other tabs put it away — they are pure pick-lists
    selectSearchTab(key) {
      this.searchTab = key;

      if (!this.isMobileView) {
        return;
      }
      const field = this.$refs.searchSuggestField;
      const input = field ? field.querySelector("input") : null;

      if (key === "address") {
        // Focus the in-panel address input (that's where you type on mobile)
        this.$nextTick(() => {
          const inline = this.$refs.addressInlineInput;
          const el = inline && inline.$el ? inline.$el.querySelector("input") : null;

          (el || input) && (el || input).focus();
        });
      } else if (input) {
        input.blur();
      }
    },
    // Mobile: the first touch on the panel dismisses the keyboard — the
    // visualViewport listener then grows the panel into the freed space
    onPanelTouch() {
      if (!this.isMobileView) {
        return;
      }
      const active = document.activeElement;

      if (active && active.tagName === "INPUT") {
        active.blur();
      }
    },
    // Mobile dropdowns: iOS never shows the native scrollbar until you
    // scroll, so the list draws its own (native one hidden): a sticky track
    // + thumb sized from the scroll metrics, refreshed on every scroll
    onMobileDropdownActive(active, ref) {
      const dd = this.$refs[ref];
      const el = dd && dd.$el ? dd.$el.querySelector(".dropdown-content") : null;

      if (!el) {
        return;
      }
      if (!active) {
        el.removeEventListener("scroll", el._ddScrollHandler);
        return;
      }
      const update = () => {
        const visible = el.clientHeight;
        const total = el.scrollHeight;

        if (total <= visible + 1) {
          this.$set(this.ddScroll, ref, null);
          return;
        }
        // Track runs from under the list's top padding to just above the
        // pinned Terminé button (padding-top, not the sticky holder's
        // offsetTop, which grows while scrolling)
        const done = el.querySelector(".multi-select-done");
        const start = (parseFloat(getComputedStyle(el).paddingTop) || 0) + 4;
        const track = visible - start - 8 - (done ? done.offsetHeight : 0);
        const thumb = Math.max(24, Math.round((track * visible) / total));
        const top = Math.round(((track - thumb) * el.scrollTop) / (total - visible));

        this.$set(this.ddScroll, ref, { track, thumb, top });
      };

      el._ddScrollHandler = update;
      el.addEventListener("scroll", update, { passive: true });
      this.$nextTick(update);
      setTimeout(update, 60);
      setTimeout(update, 350);
    },
    // One lock shared by the suggestion panel and the mobile filter panel
    syncScrollLock() {
      const lock =
        this.isMobileView && (this.suggestionsOpen || this.showMobileFilters);

      document.documentElement.classList.toggle("suggest-scroll-lock", lock);
    },
    onDocTouch(e) {
      const field = this.$refs.searchSuggestField;

      if (field && !field.contains(e.target)) {
        this.suggestionsOpen = false;
      }
    },
    updateSuggestPanelMax() {
      const field = this.$refs.searchSuggestField;

      if (!field) {
        this.suggestPanelMax = null;
        this.suggestPanelFixedTop = null;
        return;
      }

      const rect = field.getBoundingClientRect();

      if (this.isMobileView) {
        // Pin the panel from the search bar down to the top of the keyboard
        // (visualViewport height excludes it on iOS; on Android the layout
        // resizes thanks to interactive-widget=resizes-content)
        const vView = window.visualViewport;
        const top = Math.max(0, Math.round(rect.bottom + 4));
        const visibleBottom = vView ? vView.offsetTop + vView.height : window.innerHeight;

        this.suggestPanelFixedTop = top;
        this.suggestPanelFixedHeight = Math.max(140, Math.floor(visibleBottom - top - 8));
        this.suggestPanelMax = null;
        return;
      }

      const vv = window.visualViewport;

      this.suggestPanelFixedTop = null;
      // Visible bottom edge (layout-viewport coords) minus the input's
      // bottom = the room the panel really has above the keyboard
      this.suggestPanelMax = vv
        ? Math.max(120, Math.min(320, Math.floor(vv.offsetTop + vv.height - rect.bottom - 16)))
        : null;
    },
    maybeFetchAddresses() {
      const q = this.searchText || "";

      if (!["address", "all"].includes(this.searchTab) || q.length < 2) {
        this.addressSuggestions = [];
        return;
      }
      this.debouncedFetchAddresses();
    },
    // Comma-separated agent ids when the listing is scoped to agent(s) —
    // suggestions then only count that agent's listings
    suggestionAgentsParam() {
      const ids = this.parameters.filter.rent_agent_id;

      return ids.length ? ids.join(",") : undefined;
    },
    fetchRegionOptions() {
      this.$http
        .get(this.$laravel.route("public.regions"), {
          params: { agents: this.suggestionAgentsParam() },
        })
        .then((response) => {
          this.regionOptions = response.data.regions;
          this.cityOptions = response.data.cities;
        });
    },
    fetchAddresses() {
      const q = this.searchText || "";

      if (q.length < 2) {
        return;
      }
      this.$http
        .get(this.$laravel.route("public.addresses"), {
          params: { q, agents: this.suggestionAgentsParam() },
        })
        .then((response) => {
          this.addressSuggestions = response.data.data;
        });
    },
    toggleRegion(id) {
      const list = this.parameters.filter.region_id;
      const i = list.indexOf(id);

      if (i === -1) {
        list.push(id);
        // The suggestion came from the typed text: reset it for the next one
        this.searchText = null;
      } else {
        list.splice(i, 1);
      }
    },
    onSearchBlur() {
      setTimeout(() => {
        this.searchFocused = false;
        // Desktop keeps the blur-closes-panel behavior; on mobile the panel
        // survives the blur (keyboard dismissal) and closes on outside tap
        if (!this.isMobileView) {
          this.suggestionsOpen = false;
        }
      }, 150);
    },
    // Red banner label: "Mois gratuit" for free-month promos, else "Promotion".
    promotionBanner(promo) {
      return promo && promo.type === "free_month"
        ? this.$t("promotion_free_month_banner")
        : this.$t("promotion").capitalize();
    },
    showModalInclusion(inclusion) {
      this.inclusions = inclusion;
      this.isShownModalInclusion = true;
    },
    showModalPromotion(promo) {
      this.promotionModal = promo;
      this.isShownModalPromotion = true;
    },
    closeModalPromotion() {
      this.isShownModalPromotion = false;
      this.promotionModal = null;
    },
    closeModalInclusion() {
      this.isShownModalInclusion = false;
      this.inclusions = [];
    },
    // Mirror the listing state into the URL (replace, not push, so the history
    // only holds one listing entry) — back from a property restores everything.
    syncQuery() {
      const f = this.parameters.filter;
      const query = {};

      if (f.type.length) query.t = f.type.join(",");
      if (f.region_id.length) query.r = f.region_id.join(",");
      if (f.city.length) query.city = f.city.join(",");
      if (f.rent_agent_id.length) {
        query.agent = (this.$laravel.publicRentAgents || [])
          .filter((a) => f.rent_agent_id.includes(a.id))
          .map((a) => agentSlug(a.name))
          .join(",");
      }
      if (this.priceRange[0] > 0 || this.priceRange[1] < this.priceCeiling) {
        query.pr = this.priceRange.join("-");
      }
      if (
        this.squareFeetRange[0] > 0 ||
        this.squareFeetRange[1] < this.squareFeetCeiling
      ) {
        query.sf = this.squareFeetRange.join("-");
      }
      if (this.availabilityMode !== "flexible") query.am = this.availabilityMode;
      if (this.availabilityMode === "fixed" && f.availability_date) {
        query.ad = f.availability_date;
      }
      if (f.cat_tolerance) query.cat = "1";
      if (f.dog_tolerance) query.dog = "1";
      if (f.appliances) query.app = "1";
      if (f.inclusions.length) query.inc = f.inclusions.join(",");
      if (f.has_washer_and_dryer_entry) query.wd = "1";
      if (f.has_laundry_room) query.lr = "1";
      if (this.parameters.search) query.q = this.parameters.search;
      if (this.sortMode !== "recent") query.s = this.sortMode;
      if (this.parameters.page > 1) query.p = String(this.parameters.page);
      if (this.viewMode === "map") query.view = "map";

      const current = this.$route.query;
      const unchanged =
        Object.keys(query).length === Object.keys(current).length &&
        Object.keys(query).every((k) => String(current[k]) === query[k]);

      if (!unchanged) {
        this.$router.replace({ query }).catch(() => {});
      }
    },
    loadData() {
      this.loading = true;
      this.syncQuery();

      if (this.viewMode === "map") {
        this.fetchMapItems();
      }

      // Guard against out-of-order responses: only the latest request may
      // update the results (rapid typing/filtering fires overlapping requests).
      const requestId = ++this.lastRequestId;

      this.$http
        .get(this.$laravel.route("properties-list"), {
          params: this.allParameters(),
        })
        .then((response) => {
          if (requestId !== this.lastRequestId) {
            return;
          }

          this.properties = response.data.data;
          this.loading = false;
          this.pagination = response.data.meta;
        })
        .catch((error) => {
          if (requestId === this.lastRequestId) {
            this.loading = false;
          }
        });
    },
    allParameters() {
      var internalParameters = Object.entries(this.parameters).reduce(
        (a, [k, v]) => (v == null ? a : ((a[k] = v), a)),
        {}
      );

      // Don't send an empty search term
      if (internalParameters.search === "") {
        delete internalParameters.search;
      }

      // Expand the "6.5 et +" pseudo-type into the real types for the API
      // (without mutating the reactive filter object).
      const type = internalParameters.filter && internalParameters.filter.type;
      if (type && type.includes("TYPE_6_5_PLUS")) {
        internalParameters.filter = {
          ...internalParameters.filter,
          type: type
            .filter((t) => t !== "TYPE_6_5_PLUS")
            .concat(["TYPE_6_5", "TYPE_7_5", "TYPE_8_5", "TYPE_9_5"]),
        };
      }

      return internalParameters;
    },
    openContactModal(propertyId) {
      this.contactPropertyId = propertyId;
      this.isShownModalContact = true;
    },
  },
};
</script>

<style scoped lang="scss">
@import "~/scss/variables.scss";

.is-min-height-60 {
  min-height: 60px;
}

// Multi-select dropdowns (type / region)
// Keep the scrollbar visible (touch browsers hide overlay scrollbars, making
// the list look non-scrollable on mobile).
.multi-select-dropdown ::v-deep .dropdown-content {
  scrollbar-width: thin;
  scrollbar-color: #c3c3c3 transparent;

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c9c9c9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

// Mobile: always show a scrollbar on the lists (Quartier is long) so it is
// obvious there is more below, and keep a Terminé button pinned at the end
@media screen and (max-width: 768px) {
  // Native scrollbar hidden (iOS only shows it while scrolling); the list
  // draws its own, always visible
  .multi-select-dropdown ::v-deep .dropdown-content {
    max-height: 60vh !important;
    overflow-y: auto !important;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
  }

  .multi-select-dropdown ::v-deep .dd-scroll-holder {
    position: sticky;
    top: 0;
    height: 0;
    padding: 0 !important;
    overflow: visible;
    z-index: 2;
    pointer-events: none;
  }

  .dd-scroll-track {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 5px;
    border-radius: 3px;
    background: #e2e2e2;
  }

  .dd-scroll-thumb {
    width: 100%;
    border-radius: 3px;
    background: #7d7d7d;
  }

  .multi-select-dropdown ::v-deep .multi-select-done {
    position: sticky;
    bottom: 0;
    background: #fff;
    border-top: 1px solid #ececec;
    padding: 8px;
  }

  .multi-select-done-btn {
    display: block;
    width: 100%;
    padding: 11px 16px;
    background: #131627;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    border: none;
    border-radius: 6px;
    cursor: pointer;
  }
}

.multi-select-trigger {
  .multi-select-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// Narrow filter column on desktop (about as wide as the longest checkbox
// label); the results grid gets the leftover width. Mobile stacks full-width.
@media screen and (min-width: 769px) {
  .filter-width-column {
    flex: none;
    width: 270px;
  }
}

// Search suggestions: region panel + selected chips (Rentals.ca style)
.search-suggest {
  position: relative;
}

.search-suggest-panel {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 40;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 10px 24px rgba(19, 22, 39, 0.12);
  max-height: 320px;
  overflow-y: auto;
  // Panel scroll must not chain to the page (mobile especially)
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
}

@media only screen and (max-width: 768px) {
  // With the on-screen keyboard open the visible viewport shrinks; dvh
  // tracks it, so the panel never extends under the keyboard and its
  // internal scroll can actually reach the last quartiers.
  .search-suggest-panel {
    max-height: min(320px, 38dvh);
  }
}

.search-tabs {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 4px;
}

.search-tab {
  flex: 1;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 10px 8px;
  font-size: 14px;
  font-weight: 600;
  color: #7a7a7a;
  cursor: pointer;

  &.is-active {
    color: #131627;
    border-bottom-color: #131627;
  }
}

// Mobile: "Rechercher" pinned at the bottom of the scrolling panel
.search-apply-btn {
  position: sticky;
  bottom: 0;
  display: block;
  width: 100%;
  padding: 13px 16px;
  background: #131627;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  margin-top: 4px;
}

.search-suggest-empty {
  padding: 14px;
  color: #7a7a7a;
  font-size: 14px;
}

// In-panel address input: seamless row, not a boxed form field
// Group headers in the "Tous" tab (Quartiers / Villes / Adresses)
.search-suggest-group {
  padding: 10px 14px 2px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $grey;

  &:not(:first-child) {
    border-top: 1px solid #f0f1f3;
    margin-top: 4px;
  }
}

.search-suggest-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 15px;

  &:hover {
    background: #f4f5f7;
  }
}

.search-suggest-name {
  display: flex;
  align-items: center;
}

.search-suggest-count {
  color: #7a7a7a;
  font-size: 0.85rem;
  font-weight: 600;
}

.region-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.region-chip {
  display: inline-flex;
  align-items: center;
  background: $white-dark;
  border-radius: 14px;
  padding: 3px 10px;
  font-size: 13px;
  font-weight: 600;
}

// Uniform look for every filter label (top bar + sidebar + section headers):
// same font, size, colour, and ALL-CAPS via CSS so translations stay intact.
.filter-label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #7a7a7a;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
}

// "(multiple selection)" hint next to the field label
.multi-select-hint {
  font-weight: 400;
  font-size: 0.75rem;
  color: #7a7a7a;
  text-transform: none;
  letter-spacing: normal;
}

// Selected-item check mark, pushed to the right of each dropdown option
.multi-select-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .multi-select-check {
    margin-left: 0.75rem;
    // Selected items get an is-active (dark) background from Buefy, so inherit
    // the item's text colour (white when active) to stay visible.
    color: inherit;
  }
}

// Buefy reserves 3rem of right padding on dropdown items (vs 1rem on the left),
// which pushes our check mark too far from the edge. Match the left padding so
// the check mark sits with the same gap the text has on the left.
.multi-select-dropdown ::v-deep .dropdown-item {
  padding-right: 1rem;
}

// Results loading spinner
.results-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  color: $primary;

  .results-loader-icon {
    animation: results-spin 0.8s linear infinite;
  }
}

@keyframes results-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// "Disponibilité" filter — stack the 3 mode options vertically
.availability-modes {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.6rem;
}

// Hero band under the nav — same navy family as .navbar-public (#131627),
// with a soft gradient and a faint geometric watermark echoing the logo.
// Same navy as .navbar-public so the nav and the hero read as one block,
// with a hairline separator in the kicker grey between the two
.public-hero {
  position: relative;
  overflow: hidden;
  background: #131627;
  border-top: 0.5px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  padding: 1.4rem 1.5rem 1.5rem;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    right: -40px;
    transform: translateY(-50%);
    width: 460px;
    height: 340px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='460' height='340' viewBox='0 0 460 340' fill='none' stroke='white' stroke-width='1.5'%3E%3Cpath d='M60 300 L150 130 L240 300 Z'/%3E%3Cpath d='M240 300 L330 130 L420 300 Z'/%3E%3Cpath d='M150 130 L240 -40 L330 130 Z'/%3E%3Cpath d='M240 -40 L330 130 L420 -40 Z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    opacity: 0.08;
    pointer-events: none;
  }

  .public-hero-flex {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    z-index: 1;
  }

  // Agent hero: big photo + identity on the left, the agent takes the
  // title spot; "Voir tous les logements" pill on the right
  .hero-agent-identity {
    display: flex;
    align-items: center;
    gap: 22px;
  }

  .hero-agent-photo {
    width: 96px;
    height: 96px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid rgba(255, 255, 255, 0.85);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
    flex-shrink: 0;
  }

  .hero-agent-kicker {
    margin-top: 0;
    margin-bottom: 0.4rem;
  }

  .hero-all-listings {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 1rem;
    padding: 0.65rem 1.25rem;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.45);
    border-radius: 999px;
    color: #fff;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  .public-hero-kicker {
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 0.5rem;
  }

  .public-hero-title {
    font-family: $family-sans-display;
    font-size: clamp(2rem, 4vw, 2.9rem);
    font-weight: 700;
    line-height: 1.1;
    color: #fff;
  }
}

// Agent page only (desktop): same navy family but a clearly lighter shade
// than the regular hero (#131627), with a solid white button that pops on it
.public-hero.is-agent-hero {
  @media only screen and (min-width: 769px) {
    background: #2b3364;

    .hero-all-listings {
      background: #fff;
      border: 1px solid #fff;
      color: #131627;
      font-size: 1rem;
      font-weight: 700;
      padding: 0.85rem 1.6rem;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);

      &:hover {
        background: #e8eaf2;
      }
    }
  }
}

@media only screen and (max-width: 768px) {
  .public-hero {
    padding: 1rem 1.25rem 1.1rem;
  }

  // Trier and Filtres share one row (50/50); the mobile Liste|Carte toggle
  // is rendered further down, after the filter panel.
  .column.sort-column {
    width: 50% !important;
    flex: none !important;
  }

  .sort-column ::v-deep .dropdown,
  .sort-column ::v-deep .dropdown-trigger {
    width: 100%;
  }

  .sort-trigger {
    background-color: #fff !important;
    border: 1px solid #dbdbdb !important;
    color: #4a4a4a !important;
    justify-content: center !important;
    gap: 8px;

    .multi-select-label,
    .icon {
      color: #4a4a4a !important;
    }
  }

  // Filtres toggle: same light look
  .mobile-filters-btn {
    background-color: #fff !important;
    border: 1px solid #dbdbdb !important;
    color: #4a4a4a !important;
  }

  // Liste|Carte toggle: full width; the selected side matches the sort
  // trigger / Filtres color. Higher specificity than the base rule (it
  // comes later in the file and would otherwise win).
  .view-toggle {
    width: 100%;
  }

  .view-toggle .view-toggle-btn {
    flex: 1;
    justify-content: center;

    &.is-active {
      background: $grey-darker;
    }
  }

  // Uniform 12px rhythm: search -> sort -> Filtres. Bulma stacks .columns
  // below the tablet breakpoint, so force the flex row here — that's what
  // lets Trier and Filtres actually share one line.
  .search-topbar {
    margin-bottom: 0 !important;
    display: flex;
    flex-wrap: wrap;

    .column {
      padding-top: 0;
      padding-bottom: 12px;
      width: 100%;
      flex: none;
    }
  }

  .sort-spacer-label {
    display: none;
  }

  .public-hero .public-hero-flex {
    flex-wrap: wrap;
  }

  // Mobile agent hero: photo above the name, everything centered
  .public-hero .hero-agent-identity {
    flex-direction: column;
    align-items: center;
    text-align: center;
    width: 100%;
    gap: 10px;
  }

  .public-hero .hero-agent-photo {
    width: 68px;
    height: 68px;
  }

  .public-hero .hero-all-listings {
    margin-left: 0;
    margin-top: 0.9rem;
    width: 100%;
    justify-content: center;
  }
}

// Promotion — red banner, struck regular price, and red pastille
$promotion-red: #e02b2b;
// "Nouveauté" — blue banner for announces published in the last 30 days
$new-blue: #2563eb;

// Overlay the banner on top of the image (no extra card height), keeping the
// image's rounded top corners.
.card-image-wrap {
  position: relative;
}

.promotion-banner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 5;
  background: rgba($promotion-red, 0.75);
  color: #fff;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
  text-align: center;
  padding: 5px 8px;
  border-top-left-radius: 9px;
  border-top-right-radius: 9px;
}

.banner-new {
  background: rgba($new-blue, 0.75);
}

// The display font renders line-through below the visual middle, so draw the
// strike ourselves, dead-center on the digits.
.regular-price-struck {
  position: relative;
  display: inline-block;
  color: #9a9a9a;
  font-weight: 400;
  margin-right: 0.5rem;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    // 50% of the box is below the digits' middle (descender space); shift up
    // to the numerals' visual center.
    top: calc(50% - 0.08em);
    transform: translateY(-50%);
    border-top: 2px solid #b5b5b5;
  }
}

// Left filter sidebar
.filter-sidebar {
  // Tighten the gap between slider labels and the track (Buefy's b-slider has
  // a 1em top margin) so sliders match the other label/input spacing.
  .b-slider {
    margin-top: 0.35em;
    margin-bottom: 0.5em;
  }

  // Uniform vertical rhythm: every filter group ends with the same gap,
  // whether it's a field (Région, Loyer, …) or a section (Animaux, …).
  .field:not(:last-child) {
    margin-bottom: 1.5rem;
  }

  .filter-section {
    margin-bottom: 1.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .b-checkbox.checkbox {
    display: flex;
    align-items: center;
    margin-bottom: 0.55rem;
    margin-left: 0;
  }
}

@media only screen and (max-width: 768px) {
  .responsive-column-flex {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 10px !important;
  }
  .carousel .carousel-indicator.is-inside {
    overflow: hidden;
  }
  // Filters live behind the "Filtres" toggle on mobile
  .filter-sidebar.is-collapsed-mobile {
    display: none;
  }

  // Open: full-screen frozen overlay (same pattern as the search panel);
  // pick everything, then "Terminé"
  .filter-sidebar.is-open-mobile {
    position: fixed;
    top: 3.5rem;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 45;
    width: auto !important;
    margin: 0;
    padding: 14px 16px 0;
    background: #fff;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .filters-done-btn {
    margin: 16px -16px 0;
    width: calc(100% + 32px);
  }
}
.br-0 {
  border-radius: 0 !important;
}

// features

.features {
  display: flex;
  flex-direction: column;
  // Stretch rows so the distance badge can float to the right edge
  align-items: stretch;
  justify-content: flex-start;
  gap: 10px;
}

// Distance badge, pushed to the right end of the type row
.feature-item-right {
  margin-left: auto;
  // Tighter than the standard 10px feature gap
  gap: 4px;

  // The pin glyph's visual mass is its top circle, so the icon reads as
  // sitting low — nudge it up to center it on the digits.
  .feature-icon {
    margin-top: -3px;
  }
}

.feature-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  font-size: 15px;
}

.feature-icon {
  width: 20px;
  height: 20px;
  color: $grey;
  flex-shrink: 0;
}

.map-view {
  height: 72vh;
  min-height: 420px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  margin-bottom: 2rem;
  // Isolate Leaflet's internal z-indexes (panes 400-700, controls 1000) in
  // their own stacking context, so they can't cover the page's overlays
  // (search suggestions panel, dropdowns).
  position: relative;
  z-index: 0;
}

.view-toggle {
  display: flex;
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  overflow: hidden;
}

.view-toggle-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  border: none;
  padding: 8px 14px;
  font-size: 14px;
  cursor: pointer;
  color: #4a4a4a;

  &.is-active {
    background: #131627;
    color: #fff;
  }
}

// Equal card bottoms: every card stretches to its row's height and the
// button cluster is pinned down, so a 2-line agent list on one card no
// longer knocks the neighbours out of alignment
.results-grid .card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.results-grid .card-content {
  display: flex;
  flex-direction: column;
  flex: 1;

  > :first-child {
    flex: 1;
  }
}

// Rent agents on the card: small round avatar + name, same tone as features
.card-agents {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 14px;
}

.card-agent {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
}

.card-agent-link {
  cursor: pointer;

  &:hover span:last-child {
    text-decoration: underline;
  }
}

.card-agent-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

// Button Small

.button-small-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: stretch;
  gap: 10px;
  padding: 1rem;
  border-top: 1px $white-dark solid;
}

// Desktop: Inclusions + Promotion share one line; mobile: one per line
.button-small-position {
  flex-basis: 0;
  flex-grow: 1;
  flex-shrink: 1;
}

@media only screen and (max-width: 768px) {
  .button-small-position {
    flex-basis: 100%;
    flex-shrink: 0;
  }
}

.button-small {
  display: flex;
  align-items: center;
  font-size: 14px;
  white-space: nowrap;
  border-radius: 5px;
  padding-right: 10px;
  padding-left: 10px;
  background-color: $white-dark;
}

// Text dead-center: the icon zone and an empty ::after balancer both take the
// leftover space (flex: 1), which also centers the glyph between the button's
// left border and the text.
.button-small .icon {
  opacity: 0.7 !important;
  flex: 1;
  width: auto !important;
  // Minimum breathing room between the glyph and the text on narrow buttons
  margin-right: 4px;
}

.button-small::after {
  content: "";
  flex: 1;
}

// Mobile: same 10px gap between Inclusions, Promotion and "Voir tous les
// détails" (the wrapper's bottom padding pushed the button too low).
@media screen and (max-width: 768px) {
  .button-small-wrapper {
    padding-bottom: 0;
  }

  .button-more-info {
    margin: 10px 1rem 0.75rem !important;
  }
}

// "Plus d'infos" (mobile): same grey + radius as the Inclusions pastille
.button-more-info {
  font-family: $family-sans-display;
  font-weight: 400;
  height: 3rem !important;
  border: none;
  border-radius: 5px !important;
  margin: 0.75rem 1rem;
  width: calc(100% - 2rem);
  background-color: $white-dark !important;

  &:hover {
    background-color: darken(#e5e7eb, 5%) !important;
  }
}

// Button CTA

.button-cta {
  font-family: $family-sans-display;
  font-weight: 400;
  height: 3rem !important;
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
  border-bottom-left-radius: 9px !important;
  border-bottom-right-radius: 9px !important;

  background-color: $grey-dark !important;

  &:hover {
    background-color: $grey-darker !important;
  }
}
.pagination nav.pagination {
  width: 100%;
}

// Mobile: arrows on the first row, page numbers always on their own row
// below (with a single page, Bulma otherwise fits the "1" beside the arrows)
@media only screen and (max-width: 768px) {
  .pagination nav.pagination ::v-deep .pagination-list {
    flex-basis: 100%;
    justify-content: center;
  }
}
</style>

<style lang="scss">
@import "~/scss/variables.scss";

/* Unscoped: Mapbox-generated DOM (pins & popups) */
/* While the mobile suggestion panel is open, the page behind must not
   scroll (the panel handles its own overflow) */
html.suggest-scroll-lock,
html.suggest-scroll-lock body {
  overflow: hidden;
  height: 100%;
}

.map-price-pin {
  background: #131627;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #2e3d6e;
  }
}

.map-popup .leaflet-popup-content-wrapper {
  padding: 0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(19, 22, 39, 0.25);
}

.map-popup .leaflet-popup-content {
  margin: 0;
  width: 240px !important;
}

.map-price-pin {
  display: inline-block;
  transform: translate(-50%, -50%);
}

.map-popup-card {
  display: block;
  width: 240px;
  color: inherit;

  img {
    width: 100%;
    height: 130px;
    object-fit: cover;
    display: block;
  }
}

.map-popup-body {
  padding: 10px 12px;
}

// Mirrors the card's "title is-4 has-text-weight-medium"
.map-popup-price {
  font-family: $family-sans-display;
  font-weight: 500;
  font-size: 20px;
  color: #363636;
}

.map-popup-address {
  font-size: 13px;
  color: #4a4a4a;
}

.map-popup-media {
  position: relative;
}

.map-popup-banner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  color: #fff;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-align: center;
  padding: 4px 8px;

  &.is-promo {
    background: rgba(224, 43, 43, 0.75);
  }

  &.is-new {
    background: rgba(37, 99, 235, 0.75);
  }
}

// Mirrors the card's .feature-item rows
.map-popup-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #363636;
  margin-top: 6px;
}

.map-popup-icon {
  width: 18px;
  height: 18px;
  color: $grey;
  flex-shrink: 0;
}

// Mirrors the card's .button-small Inclusions pastille
.map-popup-pastille {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: $white-dark;
  border-radius: 5px;
  font-size: 13px;
  padding: 5px 10px;
  margin-top: 10px;
  color: #363636;

  .icon {
    opacity: 0.7;
    width: auto;
    height: 18px;
  }
}

.map-popup-struck {
  position: relative;
  display: inline-block;
  color: #9a9a9a;
  font-weight: 400;
  margin-right: 0.35rem;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: calc(50% - 0.08em);
    border-top: 1.5px solid #c3c3c3;
  }
}

// "(N)" unit-count suffix on a grouped pin: same size as the amount so both
// sit on one baseline, lighter weight to stay secondary
.map-pin-more {
  font-weight: 600;
  font-size: 13px;
  opacity: 0.9;
  // Parentheses hang below the baseline: nudge the count up so it reads
  // level with the amount
  position: relative;
  top: -1px;
}

// ‹ i / n › bar under the popup card when an address has several units
.map-popup-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  border-top: 1px solid #ececec;
  background: #fafafa;
  font-size: 13px;
  color: #363636;
}

.map-popup-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 50%;
  background: #131627;
  color: #fff;
  font-size: 20px;
  line-height: 1;
  padding: 0 0 2px;
  cursor: pointer;

  &:hover {
    background: #2e3d6e;
  }
}
</style>
